// src/pages/attendance/ViewAttendance.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Drawer,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TablePagination,
  Grid,
  Paper,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import DeleteConfirmDialog from "../../DeleteConfirmDialog";
import apiEndpoints from "../../../apiconfig";

export default function ViewAttendance() {
  const [records, setRecords] = useState([]);

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    recordId: null,
  });

  const [page, setPage] = useState(0); // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(5); // Rows per page

  const [editData, setEditData] = useState(null);
  const [reasonDialog, setReasonDialog] = useState({
    open: false,
    studentId: null,
    reason: "",
  });
  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.newRecord) {
      setRecords((prev) => [location.state.newRecord, ...prev]);
    }
  }, [location.state]);

  useEffect(() => {
    fetch(`${apiEndpoints.attendance}?action=get`, {
      headers: { Authorization: "Bearer " + localStorage.getItem("token") },
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) return;

        const grouped = {};

        res.data.forEach((row) => {
          // NEW UNIQUE GROUP KEY → one record per class per date
          const key = `${row.class_guid}_${row.date}`;

          if (!grouped[key]) {
            grouped[key] = {
              id: key, // unique ID for editing
              class: row.class_name,
              class_guid: row.class_guid,
              branch_guid: row.branch_guid, // 👈 ADD THIS
              date: row.date,
              students: [],
            };
          }

          grouped[key].students.push({
            student_guid: row.student_guid,
            name:
              row.student_name ||
              `${row.first_name || ""} ${row.last_name || ""}`.trim(),
            status: row.status,
            remarks: row.remarks || "",
          });
        });

        const today = new Date().toISOString().split("T")[0];

        const filtered = Object.values(grouped).filter(
          (rec) => rec.date <= today,
        );

        setRecords(filtered);
      });
  }, []);

  /* ------------------------------
      OPEN EDIT DRAWER
  ------------------------------ */
  const openEdit = (record) => {
    setEditData({
      ...record,
      // attendance map keyed by student_guid (not id)
      attendance: record.students.reduce((acc, stu) => {
        const key = stu.student_guid;
        acc[key] = {
          status: stu.status || "present",
          reason: stu.remarks || stu.reason || "",
          check_in: stu.check_in ? stu.check_in.substring(0, 5) : "",
          check_out: stu.check_out ? stu.check_out.substring(0, 5) : "",
        };
        return acc;
      }, {}),
    });
  };

  /* ------------------------------
      UPDATE Attendance
  ------------------------------ */
  const handleUpdate = () => {
    const payload = {
      id: editData.id,
      date: editData.date,
      branch_guid: editData.branch_guid,
      class_guid: editData.class_guid,
      records: Object.keys(editData.attendance).map((student_guid) => ({
        student_guid,
        status: editData.attendance[student_guid].status,
        remarks: editData.attendance[student_guid].reason || "",
        check_in: editData.attendance[student_guid].check_in || "",
        check_out: editData.attendance[student_guid].check_out || "",
      })),
    };
    fetch(`${apiEndpoints.attendance}?action=update`, {
      method: "POST", // POST to update/insert
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((res) => {
        if (!res.status) {
          showSnackbar(res.message, "error");
          return;
        }

        showSnackbar("Attendance updated successfully!", "success");

        // update UI without reload
        setRecords((prev) =>
          prev.map((r) =>
            r.id === editData.id
              ? {
                  ...r,
                  date: editData.date,
                  students: r.students.map((stu) => {
                    const editAtt = editData.attendance[stu.student_guid];
                    let total_hours = null;
                    if (editAtt.status === "present" && editAtt.check_in && editAtt.check_out) {
                      const [h1, m1] = editAtt.check_in.split(":").map(Number);
                      const [h2, m2] = editAtt.check_out.split(":").map(Number);
                      const diffMinutes = (h2 * 60 + m2) - (h1 * 60 + m1);
                      total_hours = diffMinutes > 0 ? Number((diffMinutes / 60).toFixed(2)) : 0;
                    }
                    return {
                      ...stu,
                      status: editAtt.status,
                      check_in: editAtt.check_in,
                      check_out: editAtt.check_out,
                      total_hours,
                      remarks: editAtt.reason || "",
                    };
                  }),
                }
              : r,
          ),
        );

        setEditData(null);
      });
  };

  /* ------------------------------
      DELETE (Soft Delete)
      ----------------------------*/
  const handleDelete = () => {
    const { date, class_guid, branch_guid } = deleteDialog.recordId;

    if (!date || !class_guid) {
      showSnackbar("Invalid delete payload", "error");
      return;
    }

    fetch(
      `${apiEndpoints.attendance}?action=delete&date=${date}&class_guid=${class_guid}&branch_guid=${branch_guid}`,
      {
        method: "DELETE",
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      },
    )
      .then((res) => res.json())
      .then((res) => {
        if (res.status) {
          showSnackbar("Attendance deleted!", "success");

          setRecords((prev) =>
            prev.filter(
              (r) => !(r.date === date && r.class_guid === class_guid),
            ),
          );
        } else {
          showSnackbar(res.message, "error");
        }

        setDeleteDialog({ open: false, recordId: null });
      });
  };

  /* ------------------------------
      TOGGLE ATTENDANCE
  ------------------------------ */
  const toggleAttendance = (stuId, status) => {
    if (status === "absent") {
      setReasonDialog({ open: true, studentId: stuId, reason: "" });
      return;
    }

    // If changing to present → clear reason
    setEditData((prev) => ({
      ...prev,
      attendance: {
        ...prev.attendance,
        [stuId]: {
          status: "present",
          reason: "",
        },
      },
    }));
  };

  /* ------------------------------
      SAVE ABSENT REASON
  ------------------------------ */
  const saveReason = () => {
    const { studentId, reason } = reasonDialog;

    setEditData((prev) => ({
      ...prev,
      attendance: {
        ...prev.attendance,
        [studentId]: { status: "absent", reason: reason || "" },
      },
    }));

    showSnackbar("Reason saved", "info");
    setReasonDialog({ open: false, studentId: null, reason: "" });
  };

  /* ------------------------------
      PAGINATION HANDLERS
  ------------------------------ */
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.25)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
                radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%)
              `,
          pointerEvents: "none",
        },
        p: 3,
      }}
    >
      <Grid
        items
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4" fontWeight={700} mb={3}>
          Attendance Records
        </Typography>

        <Button
          variant="contained"
          sx={{ mb: 2, borderRadius: 999, px: 3 }}
          onClick={() => navigate("/teacher/mark-attendance")}
        >
          Mark Attendance
        </Button>
      </Grid>
      {/* TABLE CARD */}
      <Card
        sx={{
          p: 2,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.5)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.5)",
          boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Class</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Present</TableCell>
              <TableCell>Absent</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {records
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((rec) => {
                const presentCount = rec.students.filter(
                  (s) => s.status === "present",
                ).length;

                const absentCount = rec.students.filter(
                  (s) => s.status === "absent",
                ).length;

                return (
                  <TableRow key={rec.id}>
                    <TableCell>{rec.class}</TableCell>
                    <TableCell>{rec.date}</TableCell>
                    <TableCell>{rec.students.length}</TableCell>

                    <TableCell>
                      <Chip
                        label={presentCount}
                        color="success"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={absentCount}
                        color="error"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <IconButton onClick={() => openEdit(rec)}>
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() =>
                          setDeleteDialog({
                            open: true,
                            recordId: {
                              date: rec.date,
                              class_guid: rec.class_guid,
                              branch_guid: rec.branch_guid, // 👈 ADD THIS
                            },
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        {/* PAGINATION */}
        <TablePagination
          component="div"
          count={records.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* EDIT PANEL (ClassSetup style) */}
      {editData && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "rgba(15,23,42,0.45)",
            backdropFilter: "blur(6px)",
            display: "flex",
            justifyContent: "flex-end",
          }}
          onClick={() => setEditData(null)}
        >
          {/* RIGHT PANEL CONTAINER */}
          <Box
            sx={{
              width: { xs: "100%", sm: "75%", md: "55%", lg: "45%" },
              height: "100vh",
              p: { xs: 1.5, sm: 2, md: 3 },
              display: "flex",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Paper
              elevation={6}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: { xs: 3, md: 4 },
                p: { xs: 2.5, sm: 3, md: 4 },
                bgcolor: "rgba(255,255,255,0.97)",
                backdropFilter: "blur(18px)",
                boxShadow:
                  "0 18px 45px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(148, 163, 184, 0.18)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                animation: "slideIn 0.35s ease",
              }}
            >
              {/* CLOSE BUTTON */}
              <IconButton
                onClick={() => setEditData(null)}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  bgcolor: "rgba(15,23,42,0.05)",
                  "&:hover": { bgcolor: "rgba(15,23,42,0.12)" },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* HEADER */}
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mb: 0.5, mt: { xs: 1, sm: 0 } }}
              >
                📋 Attendance Details
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Roster details for this class and date.
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {/* SCROLLABLE AREA */}
              <Box sx={{ flex: 1, overflowY: "auto", pr: 1, pt: 1 }}>
                {/* DATE INPUT */}
                <TextField
                  fullWidth
                  type="date"
                  label="Date"
                  value={editData.date}
                  disabled
                  sx={{ mb: 3 }}
                  InputLabelProps={{ shrink: true }}
                />

                {/* STUDENT LIST */}
                {editData.students.map((stu) => {
                  const att = editData.attendance[stu.student_guid] || { status: "present", check_in: "", check_out: "" };
                  const isPresent = att.status === "present";
                  
                  // Calculate total hours dynamically
                  let displayHours = "";
                  if (isPresent && att.check_in && att.check_out) {
                    const [h1, m1] = att.check_in.split(":").map(Number);
                    const [h2, m2] = att.check_out.split(":").map(Number);
                    const diffMinutes = (h2 * 60 + m2) - (h1 * 60 + m1);
                    if (diffMinutes > 0) {
                      displayHours = (diffMinutes / 60).toFixed(2) + " hrs";
                    } else {
                      displayHours = "0 hrs";
                    }
                  }

                  return (
                    <Box
                      key={stu.student_guid}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        mb: 2,
                        borderRadius: 3,
                        background: "rgba(255,255,255,0.7)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        border: "1px solid #E2E8F0"
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight={600}>{stu.name}</Typography>
                        
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {isPresent ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Present"
                              color="success"
                              size="small"
                              sx={{ borderRadius: 2 }}
                            />
                          ) : (
                            <Chip
                              icon={<CancelIcon />}
                              label="Absent"
                              color="error"
                              size="small"
                              sx={{ borderRadius: 2 }}
                            />
                          )}
                        </Box>
                      </Box>

                      {isPresent && (
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {att.check_in ? (
                            <Chip label={`In: ${att.check_in}`} variant="outlined" size="small" />
                          ) : (
                            <Chip label="No Check-In" variant="outlined" size="small" disabled />
                          )}
                          {att.check_out ? (
                            <Chip label={`Out: ${att.check_out}`} variant="outlined" size="small" />
                          ) : (
                            <Chip label="No Check-Out" variant="outlined" size="small" disabled />
                          )}
                          {displayHours && (
                            <Chip
                              label={displayHours}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Box>
                      )}

                      {!isPresent && att.reason && (
                        <Typography variant="body2" color="error" sx={{ fontStyle: "italic" }}>
                          Reason: {att.reason}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* ACTIONS */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setEditData(null)}
                  sx={{ borderRadius: 999, px: 4 }}
                >
                  Close
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      <style>
        {`
@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
`}
      </style>

      {/* REASON DIALOG */}
      <Dialog
        open={reasonDialog.open}
        onClose={() => setReasonDialog({ open: false })}
      >
        <DialogTitle>Reason for Absence</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            mt={1}
            label="Reason"
            value={reasonDialog.reason}
            onChange={(e) =>
              setReasonDialog((prev) => ({ ...prev, reason: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog({ open: false })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveReason}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteConfirmDialog
        open={deleteDialog.open}
        title="Delete Attendance Record"
        message="Are you sure you want to delete this attendance entry?"
        onClose={() => setDeleteDialog({ open: false, recordId: null })}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
